import type { PopupResultWire } from './types';

/**
 * O popup do Embedded Signup da Meta (spec 2026-09-29 §6.1; formatos nas fontes B.1).
 *
 * Duas coisas chegam por dois canais: os **IDs** (`postMessage` da janela da Meta, evento
 * `WA_EMBEDDED_SIGNUP`) e o **`code`** (callback do `FB.login`). O Engine quer os dois num só
 * `POST …/result`, e o `code` vale 30 s — então este módulo combina e devolve UM objeto, e
 * quem chama posta imediatamente.
 *
 * Só o que é puro está coberto por teste unitário; o SDK é carregado sob demanda (nunca no
 * `index.html`) e só quando alguém clica «Conectar».
 */

export interface SignupMessage {
  event: string;
  data: Record<string, unknown>;
}

const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

/** Só a Meta, só em TLS: `facebook.com` ou subdomínio. `evil-facebook.com` não passa. */
export function isFacebookOrigin(origin: string): boolean {
  try {
    const u = new URL(origin);
    if (u.protocol !== 'https:') return false;
    return u.hostname === 'facebook.com' || u.hostname.endsWith('.facebook.com');
  } catch {
    return false;
  }
}

export function parseSignupMessage(data: unknown): SignupMessage | null {
  let obj: unknown = data;
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (typeof obj !== 'object' || obj === null) return null;
  const o = obj as { type?: unknown; event?: unknown; data?: unknown };
  if (o.type !== 'WA_EMBEDDED_SIGNUP' || typeof o.event !== 'string') return null;
  const payload =
    typeof o.data === 'object' && o.data !== null ? (o.data as Record<string, unknown>) : {};
  return { event: o.event, data: payload };
}

function str(v: unknown): string | undefined {
  // A Meta manda alguns campos como número (o `error_id`): antes eles eram descartados
  // (revisão de 30/Set, menor 5).
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** O que se monta antes de limpar: `undefined` permitido aqui, nunca no corpo que sai. */
type PopupResultSolto = { event: PopupResultWire['event'] } & Partial<
  Record<Exclude<keyof PopupResultWire, 'event'>, string | undefined>
>;

/** O schema do Engine é `.strict()`: chave com `undefined` ainda é chave. Tira. */
function semUndefined(o: PopupResultSolto): PopupResultWire {
  return Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined),
  ) as unknown as PopupResultWire;
}

/**
 * `code` presente ⇒ sucesso (com os IDs se o `message` chegou; sem eles o Engine devolve
 * `missing-asset-ids` e a tela oferece reabrir). Sem `code` ⇒ o que a Meta disse (CANCEL/ERROR),
 * ou CANCEL puro quando ela não disse nada.
 */
export function combinePopupResult(
  msg: SignupMessage | null,
  code: string | null,
): PopupResultWire {
  if (code !== null) {
    const event =
      msg?.event.startsWith('FINISH') === true ? (msg.event as PopupResultWire['event']) : 'FINISH';
    return semUndefined({
      event,
      code,
      waba_id: str(msg?.data.waba_id),
      phone_number_id: str(msg?.data.phone_number_id),
      business_id: str(msg?.data.business_id),
    });
  }
  if (msg?.event === 'ERROR') {
    return semUndefined({
      event: 'ERROR',
      error_code: str(msg.data.error_id) ?? str(msg.data.error_code),
      session_id: str(msg.data.session_id),
    });
  }
  return semUndefined({ event: 'CANCEL', current_step: str(msg?.data.current_step) });
}

// ── SDK ─────────────────────────────────────────────────────────────────────

interface FbLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}
interface FbSdk {
  init(opts: { appId: string; version: string; autoLogAppEvents: boolean; xfbml: boolean }): void;
  login(cb: (r: FbLoginResponse) => void, opts: Record<string, unknown>): void;
}
declare global {
  interface Window {
    FB?: FbSdk;
    fbAsyncInit?: () => void;
  }
}

let sdkPromise: Promise<FbSdk> | null = null;

/** Insere o script uma vez e resolve quando `fbAsyncInit` roda. Idempotente. */
export function loadMetaSdk(appId: string, graphVersion: string): Promise<FbSdk> {
  if (window.FB !== undefined) return Promise.resolve(window.FB);
  if (sdkPromise !== null) return sdkPromise;
  sdkPromise = new Promise<FbSdk>((resolve, reject) => {
    window.fbAsyncInit = () => {
      const FB = window.FB;
      if (FB === undefined) {
        reject(new Error('o SDK da Meta carregou sem window.FB'));
        return;
      }
      FB.init({ appId, version: graphVersion, autoLogAppEvents: true, xfbml: false });
      resolve(FB);
    };
    const s = document.createElement('script');
    s.src = SDK_SRC;
    s.async = true;
    s.defer = true;
    s.crossOrigin = 'anonymous';
    s.onerror = () => {
      sdkPromise = null;
      reject(new Error('não foi possível carregar o SDK da Meta'));
    };
    document.head.appendChild(s);
  });
  return sdkPromise;
}

/**
 * Abre o popup e resolve com o corpo pronto para `postOnboardingResult`. Espera 300 ms após o
 * callback do login para um `message` atrasado chegar (Review Focus 1 do plano) — o `code`
 * ainda tem ~29 s de vida.
 */
export async function openEmbeddedSignup(p: {
  appId: string;
  configId: string;
  graphVersion: string;
  featureType: string | null;
}): Promise<PopupResultWire> {
  const FB = await loadMetaSdk(p.appId, p.graphVersion);
  return new Promise<PopupResultWire>((resolve) => {
    let msg: SignupMessage | null = null;
    const onMessage = (ev: MessageEvent): void => {
      if (!isFacebookOrigin(ev.origin)) return;
      const parsed = parseSignupMessage(ev.data);
      if (parsed !== null) msg = parsed;
    };
    window.addEventListener('message', onMessage);
    FB.login(
      (resp) => {
        const code = resp.authResponse?.code ?? null;
        window.setTimeout(() => {
          window.removeEventListener('message', onMessage);
          resolve(combinePopupResult(msg, code));
        }, 300);
      },
      {
        config_id: p.configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          sessionInfoVersion: '3',
          ...(p.featureType === null ? {} : { featureType: p.featureType }),
        },
      },
    );
  });
}
